import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useId, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import { coursePayment } from '@/features/courses/api/coursePayment'
import { courseKeys } from '@/features/courses/api/keys'
import { invalidateCourseAccess, invalidateCourseCards } from '@/features/courses/api/mutations'
import { useCoursePayments, useSuspenseCourseDetail } from '@/features/courses/api/queries'
import {
  COURSE_PAYMENTS_OFF_MESSAGE,
  coursePayFailure,
} from '@/features/courses/lib/coursePayFailure'
import { useStartCheckout } from '@/features/payments/api/mutations'
import { gatewayName } from '@/features/payments/lib/gateways'
import { OFFLINE_MESSAGE } from '@/features/payments/lib/payFailure'
import { readFailedAttempts, readPendingCheckout } from '@/features/payments/lib/pendingCheckout'
import { useOnline } from '@/hooks/useOnline'
import { GATEWAYS, type Gateway } from '@/schemas/payments'
import {
  CHECKOUT_COLUMNS,
  OPTIONS_COLUMN,
} from '@/screens/account/components/checkout/checkoutLayout'
import CheckoutSummary from '@/screens/account/components/checkout/CheckoutSummary'
import GatewayOptions from '@/screens/account/components/checkout/GatewayOptions'
import CheckoutCourseCard from '@/screens/courses/components/checkout/CheckoutCourseCard'
import {
  courseCheckoutSummary,
  PENDING_PAYMENT_WARNING,
  recentPendingPayment,
  REFUSAL_TITLE,
  resolveCourseCheckout,
  type CheckoutRefusal,
  type CourseCheckoutView,
} from '@/screens/courses/components/checkout/courseCheckoutModel'
import CourseCheckoutPending from '@/screens/courses/components/checkout/CourseCheckoutPending'
import CourseCheckoutUnavailable from '@/screens/courses/components/checkout/CourseCheckoutUnavailable'
import PendingPaymentGuard from '@/screens/courses/components/checkout/PendingPaymentGuard'
import { useCourseEnrol } from '@/screens/courses/components/detail/use-course-enrol'
import {
  courseCheckoutSearchSchema,
  loadCourseCheckout,
  type CourseCheckoutSearch,
} from '@/screens/courses/CourseCheckoutScreen.loader'
import { parseId } from '@/screens/courses/lib/params'

/** Back goes to the course being bought — the page the member came from. */
const HEADER = <TitleRow title="Checkout" back="/courses/$courseId" />

/**
 * How long the page may still be here after a hand-off before Pay comes back:
 * the subscription checkout's figure, for the same reason. A gateway normally
 * answers the form POST, and the page unloads, well within it; still being
 * here means the navigation was stopped or dropped. Generous on purpose: a Pay
 * pressed while a slow gateway is still answering starts a second attempt (a
 * second Pending row, though never a second charge).
 */
const HANDOFF_STALL_MS = 10_000

function CourseCheckoutError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Couldn't load checkout" />
}

export const Route = createFileRoute('/_app/courses/$courseId/checkout')({
  validateSearch: courseCheckoutSearchSchema,
  loader: loadCourseCheckout,
  pendingComponent: CourseCheckoutPending,
  errorComponent: CourseCheckoutError,
  component: CourseCheckoutRoute,
})

/**
 * Filters a `$courseId` that cannot name a course BEFORE the suspense read
 * (the loader skipped the request for it), and keys the screen by the id so
 * a move between courses remounts it instead of carrying one course's
 * refusals and gateway picks into the next.
 */
function CourseCheckoutRoute() {
  const courseId = parseId(Route.useParams().courseId)
  if (courseId === null) {
    return (
      <ScreenLayout header={HEADER} fullWidth>
        <CourseCheckoutUnavailable reason="notFound" />
      </ScreenLayout>
    )
  }
  return <CourseCheckoutScreen key={courseId} courseId={courseId} />
}

/**
 * Learn · Checkout — a paid course, on its own page (not a modal on the course
 * page) so a refresh keeps it and a failed payment's "Try again" has an
 * address to come back to. The course checkout's twin of the subscription's
 * (`screens/account/CheckoutScreen.tsx`), built from the same pieces.
 *
 * What is NOT carried over, on purpose: the plan card and "How often" (one
 * course, one price), the discount code box (mm-be takes no code for a course:
 * its start ignores one silently, so a field would only mislead), and the
 * steps rail (a course is picked on its own page, not in a flow).
 *
 * Pay POSTs `courses/{id}/checkout/` with the gateway alone, stashes the
 * attempt and hands the browser to the gateway (`useStartCheckout` with the
 * course adapter) — the charge is mm-be's `discountPrice`, never a figure from
 * this page. The page only ever offers Pay for a course the member can buy;
 * any other (not on sale, already theirs, free, gone) is a state with a way
 * on, and mm-be's own refusals at Pay turn the page into the same states.
 */
function CourseCheckoutScreen({ courseId }: { courseId: number }) {
  const search = Route.useSearch()
  const { data: course } = useSuspenseCourseDetail(courseId)
  const enrol = useCourseEnrol(courseId)
  const [refusal, setRefusal] = useState<CheckoutRefusal | null>(null)
  const view = resolveCourseCheckout(course, refusal)

  // One status line for every change this page makes under a member who did
  // not navigate: the enrolment's progress and result, the "isn't live yet"
  // answer to it, and the page a refused Pay swaps in. It is mounted before
  // any of them (a live region born with its words announces nothing) and
  // outlives the swaps, which unmount the form's own region with the form.
  const spoken = enrol.pending
    ? 'Enrolling you…'
    : enrol.enrolled
      ? "You're enrolled."
      : enrol.note?.tone === 'info'
        ? enrol.note.text
        : refusal === null
          ? ''
          : `${REFUSAL_TITLE[refusal]}.`

  return (
    <ScreenLayout header={HEADER} fullWidth>
      {view.kind === 'ready' ? (
        <CourseCheckoutForm view={view} search={search} onRefused={setRefusal} />
      ) : view.kind === 'owned' ? (
        <CourseCheckoutUnavailable
          reason="owned"
          course={view.course}
          justEnrolled={enrol.pending || enrol.enrolled}
        />
      ) : view.kind === 'free' ? (
        <CourseCheckoutUnavailable reason="free" courseId={courseId} enrol={enrol} />
      ) : view.kind === 'notOnSale' ? (
        <CourseCheckoutUnavailable reason="notOnSale" courseId={courseId} />
      ) : (
        <CourseCheckoutUnavailable reason="notFound" />
      )}
      <span role="status" className="sr-only">
        {spoken}
      </span>
    </ScreenLayout>
  )
}

/**
 * What this tab already knows about payments, read as the page opens and again
 * when Back from a gateway restores it: the attempt that left (the stash), the
 * ones the return page said failed, and what time it is. Only ever called from
 * an initialiser or an event, never during a render.
 */
const readTabPayments = () => ({
  stash: readPendingCheckout(),
  failed: readFailedAttempts(),
  now: Date.now(),
})

function CourseCheckoutForm({
  view,
  search,
  onRefused,
}: {
  view: Extract<CourseCheckoutView, { kind: 'ready' }>
  search: CourseCheckoutSearch
  onRefused: (refusal: CheckoutRefusal) => void
}) {
  const courseId = view.course.id
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const online = useOnline()

  const [gateway, setGateway] = useState<Gateway>(search.gateway ?? 'esewa')
  const [unavailable, setUnavailable] = useState<ReadonlySet<Gateway>>(() => new Set())
  const [gatewayError, setGatewayError] = useState<string | null>(null)
  const [payError, setPayError] = useState<string | null>(null)
  // Sales off is every gateway at once, so it is a state of the page rather
  // than a message that the next pick clears.
  const [paymentsOff, setPaymentsOff] = useState(false)
  const [stalled, setStalled] = useState(false)

  const start = useStartCheckout(coursePayment)
  const { isSuccess: handedOff, reset: resetStart } = start
  const leaving = start.isPending || handedOff

  // A payment for THIS course that may still settle holds Pay back until the
  // member says they mean it. mm-be only refuses a Pay for a course already
  // owned, so without this a second payment goes through and buys nothing.
  const payId = useId()
  const [tab, setTab] = useState(readTabPayments)
  const [payAgain, setPayAgain] = useState(false)
  const history = useCoursePayments()
  const pending = recentPendingPayment({
    courseId,
    stash: tab.stash,
    payments: history.data,
    failed: tab.failed,
    now: tab.now,
  })
  const guard = payAgain ? null : pending
  const guarded = guard !== null

  // "Pay again anyway" leaves with the warning, taking the focus it held, and
  // Pay is what the member is after next. After the warning is gone, so the page
  // has settled where it will scroll to.
  useEffect(() => {
    if (payAgain) document.getElementById(payId)?.focus()
  }, [payAgain, payId])

  // Back from the gateway restores this page, state and all, with the attempt
  // that left now in the stash: the member is looking at Pay again.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      setTab(readTabPayments())
      setPayAgain(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => {
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  // A hand-off that never unloaded the page gives Pay back.
  useEffect(() => {
    if (!handedOff) return
    const timer = window.setTimeout(() => {
      setStalled(true)
      resetStart()
    }, HANDOFF_STALL_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [handedOff, resetStart])

  const pickGateway = (next: Gateway) => {
    setGateway(next)
    setGatewayError(null)
    setPayError(null)
    void navigate({ search: (prev) => ({ ...prev, gateway: next }), replace: true })
  }

  const summary = courseCheckoutSummary(view)

  const pay = () => {
    setPayError(null)
    setGatewayError(null)
    setStalled(false)
    if (!online) {
      setPayError(OFFLINE_MESSAGE)
      return
    }
    start.mutate(
      { courseId, gateway },
      {
        onError: (error) => {
          const failure = coursePayFailure(error, navigator.onLine)
          switch (failure.kind) {
            case 'gateway': {
              // The pick moves to a gateway that can still be paid with, so Pay
              // stays usable; the failed one stays listed, disabled.
              const off = new Set(unavailable).add(gateway)
              const next = GATEWAYS.find((candidate) => !off.has(candidate))
              setUnavailable(off)
              if (next !== undefined) {
                setGateway(next)
                void navigate({ search: (prev) => ({ ...prev, gateway: next }), replace: true })
              }
              setGatewayError(
                next === undefined
                  ? `${gatewayName(gateway)} isn't available right now.`
                  : `${gatewayName(gateway)} isn't available right now. We've picked ${gatewayName(next)} instead.`,
              )
              return
            }
            case 'paymentsOff':
              setPaymentsOff(true)
              return
            case 'owned':
            case 'free':
            case 'notOnSale':
            case 'notFound':
              // mm-be knows something the loaded course did not. Re-read it, and
              // the card lists that still show its old price or "Buy", so the
              // course page and the catalogue agree, and stop offering Pay for
              // it here.
              void (failure.kind === 'owned'
                ? invalidateCourseAccess(queryClient, courseId)
                : Promise.all([
                    queryClient.invalidateQueries({ queryKey: courseKeys.detail(courseId) }),
                    invalidateCourseCards(queryClient),
                  ]))
              onRefused(failure.kind)
              return
            case 'message':
              setPayError(failure.text)
          }
        },
      },
    )
  }

  return (
    <>
      {/* Mounted before the warning can appear, so one that arrives late (the
          history answers after the page opens) is announced. The warning
          itself is drawn aria-hidden: this is where it is read. */}
      <p role="status" className="sr-only">
        {guarded ? PENDING_PAYMENT_WARNING : ''}
      </p>
      {guard === null ? null : (
        <PendingPaymentGuard
          found={guard}
          onPayAgain={() => {
            setPayAgain(true)
          }}
        />
      )}
      <div className={CHECKOUT_COLUMNS}>
        <div className={OPTIONS_COLUMN}>
          <CheckoutCourseCard course={view.course} />
          <GatewayOptions
            value={gateway}
            onChange={pickGateway}
            unavailable={unavailable}
            error={gatewayError}
            disabled={leaving || paymentsOff}
          />
        </div>
        <CheckoutSummary
          rows={summary.rows}
          total={summary.total}
          gatewayName={gatewayName(gateway)}
          leaving={leaving}
          blocked={paymentsOff || unavailable.has(gateway) || guarded}
          error={paymentsOff ? COURSE_PAYMENTS_OFF_MESSAGE : payError}
          stalled={stalled}
          onPay={pay}
          payId={payId}
        />
      </div>
    </>
  )
}
